const DEFAULT_CYCLE_LENGTH_DAYS = 28;
const MIN_CYCLE_LENGTH_DAYS = 21;
const MAX_CYCLE_LENGTH_DAYS = 45;
const MENSTRUATION_FALLBACK_DAYS = 5;
const LUTEAL_DAYS = 14;
const LATE_LUTEAL_DAYS = 7;
const MAX_USABLE_INTERVAL_SPREAD_DAYS = 10;
function parseDateOnly(value) {
    return new Date(`${value}T00:00:00.000Z`);
}
function toDateOnly(date) {
    return date.toISOString().slice(0, 10);
}
function addDays(value, days) {
    const date = parseDateOnly(value);
    date.setUTCDate(date.getUTCDate() + days);
    return toDateOnly(date);
}
function daysBetween(from, to) {
    return Math.round((parseDateOnly(to).getTime() - parseDateOnly(from).getTime()) / 86_400_000);
}
function median(values) {
    const sorted = [...values].sort((a, b) => a - b);
    const middle = Math.floor(sorted.length / 2);
    return sorted.length % 2 === 0
        ? Math.round(((sorted[middle - 1] ?? 0) + (sorted[middle] ?? 0)) / 2)
        : (sorted[middle] ?? DEFAULT_CYCLE_LENGTH_DAYS);
}
function clampCycleLength(length) {
    if (!Number.isFinite(length))
        return DEFAULT_CYCLE_LENGTH_DAYS;
    return Math.min(MAX_CYCLE_LENGTH_DAYS, Math.max(MIN_CYCLE_LENGTH_DAYS, Math.round(length ?? 0)));
}
function periodStarts(history) {
    return [
        ...new Set(history.filter((e) => e.eventType === 'period_start').map((e) => e.occurredOn)),
    ]
        .filter((date) => /^\d{4}-\d{2}-\d{2}$/.test(date))
        .sort();
}
function validIntervals(starts) {
    const intervals = [];
    for (let index = 1; index < starts.length; index += 1) {
        const interval = daysBetween(starts[index - 1] ?? '', starts[index] ?? '');
        if (interval >= MIN_CYCLE_LENGTH_DAYS && interval <= MAX_CYCLE_LENGTH_DAYS) {
            intervals.push(interval);
        }
    }
    return intervals;
}
function intervalQuality(intervals) {
    if (intervals.length < 3)
        return intervals.length > 0 ? 'low' : 'none';
    const spread = Math.max(...intervals) - Math.min(...intervals);
    return spread <= MAX_USABLE_INTERVAL_SPREAD_DAYS ? 'usable' : 'low';
}
function resolvedLength(starts, userSetLengthDays) {
    const intervals = validIntervals(starts);
    const quality = intervalQuality(intervals);
    if (quality === 'usable') {
        return {
            cycleLengthDays: median(intervals),
            quality,
            basedOnHistoryCycles: intervals.length,
            source: 'history',
        };
    }
    return {
        cycleLengthDays: clampCycleLength(userSetLengthDays),
        quality: starts.length > 0 ? 'low' : 'none',
        basedOnHistoryCycles: intervals.length,
        source: starts.length > 0 ? 'user_set' : 'none',
    };
}
export function predictNextPeriod(history, userSetLengthDays, asOf = toDateOnly(new Date())) {
    const starts = periodStarts(history);
    if (starts.length === 0) {
        return {
            nextPeriodStart: null,
            cycleLengthDays: null,
            quality: 'none',
            basedOnHistoryCycles: 0,
            source: 'none',
        };
    }
    const length = resolvedLength(starts, userSetLengthDays);
    let nextPeriodStart = addDays(starts[starts.length - 1] ?? asOf, length.cycleLengthDays ?? 28);
    while (nextPeriodStart <= asOf) {
        nextPeriodStart = addDays(nextPeriodStart, length.cycleLengthDays ?? 28);
    }
    return { nextPeriodStart, ...length };
}
function loggedPeriodEndForCycle(history, start) {
    const ends = history
        .filter((e) => e.eventType === 'period_end' && e.occurredOn >= start)
        .map((e) => e.occurredOn)
        .sort();
    return ends[0] ?? null;
}
function phaseForDate(history, asOf, prediction) {
    const starts = periodStarts(history).filter((date) => date <= asOf);
    const lastStart = starts[starts.length - 1];
    if (!lastStart || !prediction.nextPeriodStart || !prediction.cycleLengthDays) {
        return { phase: 'unknown', lateLutealWindow: false };
    }
    const loggedEnd = loggedPeriodEndForCycle(history, lastStart);
    const menstruationEnd = loggedEnd ?? addDays(lastStart, MENSTRUATION_FALLBACK_DAYS - 1);
    if (asOf >= lastStart && asOf <= menstruationEnd) {
        return { phase: 'menstruation', lateLutealWindow: false };
    }
    const lutealStart = addDays(prediction.nextPeriodStart, -LUTEAL_DAYS);
    const lateLutealStart = addDays(prediction.nextPeriodStart, -LATE_LUTEAL_DAYS);
    const lateLutealEnd = addDays(prediction.nextPeriodStart, -1);
    if (asOf >= lutealStart && asOf < prediction.nextPeriodStart) {
        return {
            phase: 'luteal',
            lateLutealWindow: asOf >= lateLutealStart && asOf <= lateLutealEnd,
        };
    }
    return { phase: 'follicular', lateLutealWindow: false };
}
export function currentPhase(history, asOf = toDateOnly(new Date()), userSetLengthDays) {
    const prediction = predictNextPeriod(history, userSetLengthDays, asOf);
    const phase = phaseForDate(history, asOf, prediction);
    return {
        phase: phase.phase,
        lateLutealWindow: phase.lateLutealWindow,
        asOf,
        nextPeriodStart: prediction.nextPeriodStart,
        cycleLengthDays: prediction.cycleLengthDays,
        predictionQuality: prediction.quality,
        basedOnHistoryCycles: prediction.basedOnHistoryCycles,
    };
}
//# sourceMappingURL=prediction.js.map